import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface DashboardSummary {
  readonly totalDocuments: number;
  readonly byStatus: Record<string, number>;
  readonly byClassification: Record<string, number>;
  readonly byArchiveTier: Record<string, number>;
  readonly evidenceCount: number;
  readonly activeLegalHolds: number;
  readonly pendingReviews: number;
  readonly documentsInWorkflow: number;
}

export function M23DocumentDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: DashboardSummary }>('/v1/document-dashboard', { token })
      .then((r) => { if (alive) setSummary(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <p role="alert">{error}</p>;
  if (!summary) return <p>{t('m23.dashboard.loading')}</p>;
  return (
    <main aria-label="document-dashboard">
      <h1>{t('m23.dashboard.title')}</h1>
      <section aria-label="dashboard-metrics" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
        <article aria-label="total-documents"><h2>{t('m23.dashboard.totalDocuments')}</h2><p>{summary.totalDocuments}</p></article>
        <article aria-label="evidence-count"><h2>{t('m23.dashboard.evidenceItems')}</h2><p>{summary.evidenceCount}</p></article>
        <article aria-label="active-legal-holds"><h2>{t('m23.dashboard.activeLegalHolds')}</h2><p>{summary.activeLegalHolds}</p></article>
        <article aria-label="pending-reviews"><h2>{t('m23.dashboard.pendingReviews')}</h2><p>{summary.pendingReviews}</p></article>
        <article aria-label="documents-in-workflow"><h2>{t('m23.dashboard.inWorkflow')}</h2><p>{summary.documentsInWorkflow}</p></article>
      </section>
      <section aria-label="by-status">
        <h2>{t('m23.dashboard.byStatus')}</h2>
        <table aria-label="status-breakdown">
          <thead><tr><th>{t('m23.dashboard.colStatus')}</th><th>{t('m23.dashboard.colCount')}</th></tr></thead>
          <tbody>
            {Object.entries(summary.byStatus).map(([k, v]) => (
              <tr key={k}><td>{k}</td><td>{v}</td></tr>
            ))}
          </tbody>
        </table>
      </section>
      <section aria-label="by-classification">
        <h2>{t('m23.dashboard.byClassification')}</h2>
        <table aria-label="classification-breakdown">
          <thead><tr><th>{t('m23.dashboard.colClassification')}</th><th>{t('m23.dashboard.colCount')}</th></tr></thead>
          <tbody>
            {Object.entries(summary.byClassification).map(([k, v]) => (
              <tr key={k}><td>{k}</td><td>{v}</td></tr>
            ))}
          </tbody>
        </table>
      </section>
      <section aria-label="by-archive-tier">
        <h2>{t('m23.dashboard.byArchiveTier')}</h2>
        <table aria-label="archive-tier-breakdown">
          <thead><tr><th>{t('m23.dashboard.colTier')}</th><th>{t('m23.dashboard.colCount')}</th></tr></thead>
          <tbody>
            {Object.entries(summary.byArchiveTier).map(([k, v]) => (
              <tr key={k}><td>{k}</td><td>{v}</td></tr>
            ))}
          </tbody>
        </table>
      </section>
    </main>
  );
}
